import React, { useCallback, useEffect, useRef, useState } from "react";
import { UploadCloud } from "lucide-react";

/**
 * DropZone, accepts a single image file via drag-drop, click-to-upload,
 * or paste (Ctrl/Cmd-V). Calls onFile(File) once a valid image is provided.
 *
 * No upload happens. The parent uses URL.createObjectURL(file) to display.
 */
export default function DropZone({ onFile }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const accept = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") return;
    onFile(file);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setOver(false);
    accept(e.dataTransfer.files?.[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPick = (e) => accept(e.target.files?.[0]);

  // Listen for paste anywhere on the page while DropZone is mounted
  useEffect(() => {
    const onPaste = (e) => {
      const item = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith("image/"));
      if (item) accept(item.getAsFile());
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
      data-testid="sbs-dropzone"
      className="cursor-pointer rounded-xl flex flex-col items-center justify-center text-center transition-colors h-full min-h-[320px] p-8"
      style={{
        border: "2px dashed",
        borderColor: over ? "#1d4ed8" : "#e5e7eb",
        background: over ? "#dbeafe" : "#f3f4f6",
      }}>
      <UploadCloud className="w-8 h-8 mb-3" strokeWidth={1.5} style={{ color: over ? "#1d4ed8" : "#9ca3af" }} />
      <div className="text-sm font-medium" style={{ color: "#111928" }}>Drop your document here</div>
      <div className="text-xs mt-1.5" style={{ color: "#9ca3af" }}>
        or click to upload · or paste with <kbd className="px-1 rounded" style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}>Ctrl+V</kbd>
      </div>
      <div className="text-[11px] mt-4 uppercase tracking-[0.12em]" style={{ color: "#9ca3af" }}>
        JPG · PNG · HEIC · PDF
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={onPick}
        className="hidden"
        data-testid="sbs-file-input"
      />
    </div>
  );
}
